import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { deFondo } from "../security/helper/peticion-de-fondo";
import { Observable } from "rxjs";

import { UrlConstantes } from "../utilerias/constantes/urlconstantes";
import {
  SolicitudIA,
  SolicitudIADar,
  SolicitudIAReq,
  SolicitudIARes,
} from "../shared/model/iso42001/solicitud-ia.model";

/**
 * Solicitudes de IA (SIA).
 *
 * Los ARCHIVOS no viven aquí: se suben y se bajan con el almacén genérico
 * de evidencias del módulo ISO 42001 (MatrizRiesgoService), pasándole
 * tipoOrigen 'SOLICITUD_IA'. Es la misma tabla que ya sirve a los activos,
 * los riesgos y los aspectos de monitoreo; duplicarla aquí sería tener dos
 * sitios donde arreglar el mismo problema.
 */
@Injectable({ providedIn: "root" })
export class SolicitudesIaService {
  constructor(private http: HttpClient) {}

  /** Lo del propio colaborador: sus solicitudes y si además atiende. */
  mias(): Observable<SolicitudIARes> {
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_MIAS, {});
  }

  solicitar(solicitud: SolicitudIA): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { solicitud };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_SOLICITAR, req);
  }

  /**
   * La bandeja de los cinco, con filtros y paginado.
   *
   * `fondo` es para el refresco automatico: la misma consulta, pero sin
   * el spinner de pantalla completa. Sin eso, el tablero taparia la app
   * cada medio minuto sin que nadie hubiera pulsado nada.
   */
  bandeja(req: SolicitudIAReq, fondo = false): Observable<SolicitudIARes> {
    return this.http.post<SolicitudIARes>(
      UrlConstantes.SOLICITUDES_IA_BANDEJA,
      req,
      fondo ? { context: deFondo() } : {},
    );
  }

  /** Una solicitud con su historia completa. */
  detalle(idSolicitudIa: number): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_DETALLE, req);
  }

  /** Tomarla (sin responsable) o asignársela a otro de los cinco. */
  asignar(idSolicitudIa: number, idUsuarioResponsable?: number | null): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa, idUsuarioResponsable };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_ASIGNAR, req);
  }

  /**
   * Mover de estatus. El comentario es obligatorio siempre; el área del
   * catálogo ISO sólo al aprobar, porque es con la que entra al inventario.
   */
  mover(
    idSolicitudIa: number,
    estatus: string,
    comentario: string,
    idCatArea?: number | null,
  ): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa, estatus, comentario, idCatArea };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_MOVER, req);
  }

  cancelar(idSolicitudIa: number): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_CANCELAR, req);
  }

  comentar(idSolicitudIa: number, comentario: string): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa, comentario };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_COMENTAR, req);
  }

  /* ---------------- El DAR (FOR-012) ---------------- */

  /** El DAR capturado de una solicitud, o vacío si sólo se subió el archivo. */
  dar(idSolicitudIa: number): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { idSolicitudIa };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_DAR, req);
  }

  guardarDar(dar: SolicitudIADar): Observable<SolicitudIARes> {
    const req: SolicitudIAReq = { dar };
    return this.http.post<SolicitudIARes>(UrlConstantes.SOLICITUDES_IA_GUARDAR_DAR, req);
  }
}
